import { inject } from "fw";
import { Store, handle } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { AtsRepository } from "network/ats-repository";
import { NestedAggregationItem, StatusAggregationItem } from "models/aggregations";
import { AtsInterviewPortalExportHistoryFilter, AtsInterviewPortalExportHistory, Season } from "models/monitor-interview-portal-export-histories";
import { Filter, OrganizationFilter } from "models/monitor-exports";
import { LocalStorageCache } from "caching";
import { AccountType } from "models/ats-organization";

const REQUIRED_GRID_FIELDS = "*";
const AGGREGATIONS = "terms:status";
const APPLICATION_AIM_STATUS_AGGREGATIONS = "terms:metaData.aimStatus.status";
const APPLICATION_AIM_STATUS_AGGREGATIONS_RESULT_KEY = "terms_metaData.aimStatus.status";
const ORGANIZATION_LEVEL_AGGREGATIONS =
    "terms:(organizationId~500 sum:applicationCount sum:getApplicationCsvErrorsCount sum:getApplicationFullPdfErrorsCount sum:getApplicationLimitedPdfErrorsCount sum:saveFileErrorsCount sum:missingApplicationsCount sum:userCount)";

const FILTER_FIELDS_MAPPING: Map<string, string> = new Map()
    .set("organization", "organizationId")
    .set("interviewPortal", "interviewPortalId")
    .set("status", "status")
    .set("range", "metaData.dateCreatedUtc");

const APPLICATION_AIM_STATUS_COUNT_FILTER_FIELDS_MAPPING: Map<string, string> = new Map()
    .set("organization", "metaData.organizationId")
    .set("interviewPortal", "interviewPortalId")
    .set("status", "status")
    .set("range", "metaData.aimStatus.dateLastUpdatedUtc");

const CACHE_KEY = "ats-monitor-interview-portal-export-history-store";

export class PreviousPageAction {}
export class NextPageAction {}
// Client side pagination
export class OrganizationPreviousAction {}
export class OrganizationNextAction {}
export class ResetAction {}
export class RefreshAction {}
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_NUMBER = 1;
export class CollapseItemAction {
    constructor(public id: string) {}
}

export class LoadEvaluationPhaseAction {
    constructor(public id: string) {}

    public season: Season;
}

export class InterviewPortalExportHistoryFetchListAction<Filter> {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public orgFilter: OrganizationFilter = {},
        public sort: string = ""
    ) {}
}

export class InterviewPortalExportHistoryFetchOrganizationAggsAction<Filter> {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public orgFilter: OrganizationFilter = {},
        public sort: string = "",
        public loadStatusAggs: boolean = false
    ) {}

    public aggregations: OrganizationAggregations[] = [];
}

export class ApplicationAimStatusCountFetchAggsAction<Filter> {
    constructor(public filter: Filter = null) {}

    public aimStatusAggregation: StatusAggregationItem[] = [];
}

export class InterviewPortalExportHistoryResetAction {}
export class InterviewPortalExportHistoryRefreshAction {}

export type OrganizationAggregations = {
    organizationId: string;
    applicationCount: number;
    getApplicationCsvErrorsCount: number;
    getApplicationFullPdfErrorsCount: number;
    getApplicationLimitedPdfErrorsCount: number;
    saveFileErrorsCount: number;
    missingApplicationsCount: number;
    userCount: number;
};

type SavedMonitorInterviewPortalGridState<Filter> = {
    filter: Filter;
    filterCounter: number;
    search: string;
    sort: string;
    pageNumber: number;
    pageSize: number;
    previousPageToken: string;
    nextPageToken: string;
    currentPreviousPageToken: string;
    currentNextPageToken: string;
    orgFilter: OrganizationFilter;
    runDateAggInterval: string;
};

export type MonitorInterviewPortal<List, AtsInterviewPortalExportHistoryFilter> = {
    currentUserId: string;

    loaded: boolean;
    collapsedLoaded: boolean;
    errorLoading: boolean;
    errorMessage: string;
    list: List[];
    organizationList: OrganizationAggregations[];
    organizationPageData: OrganizationAggregations[];
    organizationPageNumber: number;
    organizationTotal: number;
    statusCounts: StatusAggregationItem[];
    dateBuckets: NestedAggregationItem[];
    total: number;
    filter: AtsInterviewPortalExportHistoryFilter;
    filterCounter: number;
    selectedFilter: any;
    search: string;
    sort: string;
    runDateAggInterval: string;

    previousPageToken: string;
    nextPageToken: string;
    currentPreviousPageToken: string;
    currentNextPageToken: string;
    pageSize: number;
    pageNumber: number;
    collapsedItem: string;
    updatedDate: Date;
    orgFilter: OrganizationFilter;
};

export class FetchListAction<Filter> {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public orgFilter: OrganizationFilter = {},
        public sort: string = ""
    ) {}
}

export class MonitorInterviewPortalGridStore<AtsInterviewPortalExportHistory, AtsInterviewPortalExportHistoryFilter> extends Store<
    MonitorInterviewPortal<AtsInterviewPortalExportHistory, AtsInterviewPortalExportHistoryFilter>
> {
    protected defaultState(): MonitorInterviewPortal<AtsInterviewPortalExportHistory, AtsInterviewPortalExportHistoryFilter> {
        return {
            currentUserId: null,

            loaded: false,
            collapsedLoaded: false,
            errorLoading: false,
            errorMessage: "",
            list: [],
            organizationList: [],
            organizationPageData: [],
            organizationPageNumber: DEFAULT_PAGE_NUMBER,
            organizationTotal: 0,
            statusCounts: [],
            dateBuckets: [],
            total: 0,
            filter: {} as AtsInterviewPortalExportHistoryFilter,
            filterCounter: 0,
            selectedFilter: {},
            search: null,
            sort: null,
            runDateAggInterval: "1d",
            previousPageToken: null,
            nextPageToken: null,
            currentPreviousPageToken: null,
            currentNextPageToken: null,
            pageSize: DEFAULT_PAGE_SIZE,
            pageNumber: DEFAULT_PAGE_NUMBER,
            collapsedItem: "",
            updatedDate: new Date(),
            orgFilter: { type: AccountType.Customer, tags: null }
        };
    }
}

@inject
export class MonitorInterviewPortalExportHistoriesStore extends MonitorInterviewPortalGridStore<
    AtsInterviewPortalExportHistory,
    AtsInterviewPortalExportHistoryFilter
> {
    private listLoading: boolean = false;
    private cache: LocalStorageCache = new LocalStorageCache();
    private readonly keyPostfix;

    constructor(private network: AtsRepository) {
        super();
    }
    public handleStartAction(action: StartAction): void {
        this.setState(() => ({
            ...this.defaultState(),
            currentUserId: action.user.Id
        }));
    }
    public handleCollapseItemAction(action: CollapseItemAction, callback = null): Function {
        this.setState((state) => ({
            ...state,
            collapsedItem: action.id,
            collapsedLoaded: false
        }));

        if (callback) return callback();
    }

    public handleLogoutAction(): void {
        this.setState(() => this.defaultState());
    }

    public handleRefreshAction(action: RefreshAction, callback = null): Function {
        if (!this.state?.loaded) return;

        this.setState((state) => ({
            ...state,
            loaded: false
        }));

        this.saveState();

        if (callback) return callback();
    }

    public handleResetAction(action: ResetAction, callback = null): Function {
        if (!this.state?.loaded) return;

        // this.setState(() => {
        //     return {
        //         ...this.state,
        //         filter: {} as Filter,
        //         selectedFilter: {}
        //     };
        // });

        this.saveState();

        if (callback) return callback();
    }

    public saveState(): void {
        if (this.state.currentUserId == null) return;

        const { list, loaded, statusCounts, total, orgFilter, ...rest } = this.state;

        // this.cache?.set<SavedMonitorInterviewPortalGridState<Filter>>(this.cacheKey, {
        //     ...rest,
        //     orgFilter: this.state.orgFilter
        // });
    }

    public handleFetchList(action: FetchListAction<AtsInterviewPortalExportHistoryFilter>, callback = null): Function {
        this.setState(() => ({
            ...this.state,
            filter: action.filter,
            search: action.search,
            sort: action.sort,
            loaded: false,
            collapsedLoaded: false,
            collapsedItem: "",
            errorLoading: false,
            errorMessage: "",
            pageNumber: DEFAULT_PAGE_NUMBER,
            previousPageToken: null,
            nextPageToken: null,
            currentPreviousPageToken: null,
            currentNextPageToken: null,
            orgFilter: action.orgFilter
        }));

        this.saveState();
        if (callback) return callback();
    }

    public async handleNextPageAction(action: NextPageAction, callback): Promise<Function> {
        const { nextPageToken, pageSize, pageNumber, total } = this.state;

        if (pageNumber * pageSize > total) return;

        this.setState((state) => ({
            ...state,
            loaded: false,
            collapsedItem: "",
            pageNumber: pageNumber + 1
        }));

        return callback(nextPageToken);
    }

    public async handlePreviousPage(action: PreviousPageAction, callback): Promise<Function> {
        const { previousPageToken, pageNumber } = this.state;
        if (pageNumber <= 1) return;

        this.setState((state) => ({
            ...state,
            loaded: false,
            collapsedItem: "",
            pageNumber: pageNumber - 1
        }));

        return callback(previousPageToken);
    }
    @handle(StartAction)
    private handleActionStart(action: StartAction) {
        this.handleStartAction(action);
    }

    @handle(CollapseItemAction)
    private collapseItem(action: CollapseItemAction) {
        this.handleCollapseItemAction(action);
    }

    @handle(LogoutAction)
    private logoutAction() {
        this.handleLogoutAction();
    }

    @handle(InterviewPortalExportHistoryRefreshAction)
    private async refreshAction(action: InterviewPortalExportHistoryRefreshAction) {
        await this.handleRefreshAction(action, async () => {
            await this.loadPageList();
        });
    }

    @handle(InterviewPortalExportHistoryResetAction)
    private resetAction(action: InterviewPortalExportHistoryResetAction) {
        this.handleResetAction(action, () => {
            this.loadPageList();
        });
    }

    @handle(InterviewPortalExportHistoryFetchListAction)
    private async fetchListAction(action: InterviewPortalExportHistoryFetchListAction<AtsInterviewPortalExportHistoryFilter>) {
        await this.handleFetchList(action, async () => {
            await this.loadPageList();
        });
    }

    @handle(NextPageAction)
    private nextPageAction(action: NextPageAction) {
        this.handleNextPageAction(action, async (nextPageToken) => {
            await this.loadPageData(null, nextPageToken);
        });
    }

    @handle(PreviousPageAction)
    private previousPageAction(action: NextPageAction) {
        this.handlePreviousPage(action, async (previousPageToken) => {
            await this.loadPageData(previousPageToken, null);
        });
    }

    @handle(InterviewPortalExportHistoryFetchOrganizationAggsAction)
    private async fetchOrgAggregationsAction(action: InterviewPortalExportHistoryFetchOrganizationAggsAction<AtsInterviewPortalExportHistoryFilter>) {
        await this.handleFetchList(action, async () => {
            const { pageSize, sort, search, filter } = this.state;
            if (action.loadStatusAggs) {
                const historyResultsStatusAggs = await this.network.atsInterviewPortalExportHistoryList(
                    this.getWholeFilter(false, FILTER_FIELDS_MAPPING),
                    search,
                    sort,
                    null,
                    null,
                    REQUIRED_GRID_FIELDS,
                    AGGREGATIONS,
                    true,
                    this.getOrgFilter(),
                    pageSize
                );
                const newStatusCounts: [] = historyResultsStatusAggs.Aggregations?.["terms_status"]?.Items;
                this.setState((state) => ({
                    ...state,
                    statusCounts: newStatusCounts ?? [],
                    updatedDate: new Date()
                }));
            }

            const historyResultsOrganizationAggs = await this.network.atsInterviewPortalExportHistoryList(
                this.getWholeFilter(true, FILTER_FIELDS_MAPPING),
                search,
                sort,
                null,
                null,
                REQUIRED_GRID_FIELDS,
                ORGANIZATION_LEVEL_AGGREGATIONS,
                true,
                this.getOrgFilter(),
                pageSize
            );

            if (historyResultsOrganizationAggs.Aggregations) {
                action.aggregations = (historyResultsOrganizationAggs.Aggregations["terms_organizationId"]?.Items ?? []).map((agg) => {
                    return {
                        organizationId: agg.Key as string,
                        applicationCount: agg.Aggregations.sum_applicationCount.Value as number,
                        getApplicationCsvErrorsCount: agg.Aggregations.sum_getApplicationCsvErrorsCount.Value as number,
                        getApplicationFullPdfErrorsCount: agg.Aggregations.sum_getApplicationFullPdfErrorsCount.Value as number,
                        getApplicationLimitedPdfErrorsCount: agg.Aggregations.sum_getApplicationLimitedPdfErrorsCount.Value as number,
                        saveFileErrorsCount: agg.Aggregations.sum_saveFileErrorsCount.Value as number,
                        missingApplicationsCount: agg.Aggregations.sum_missingApplicationsCount.Value as number,
                        userCount: agg.Aggregations.sum_userCount.Value as number
                    };
                });
            }

            if (filter.organization) {
                await this.loadPageList();
            }

            this.setState((state) => ({
                ...state,
                loaded: true,
                organizationList: action.aggregations,
                organizationTotal: action.aggregations.length,
                organizationPageData: action.aggregations.slice(0, DEFAULT_PAGE_SIZE),
                organizationPageNumber: DEFAULT_PAGE_NUMBER,
                updatedDate: new Date()
            }));
        });
    }

    @handle(ApplicationAimStatusCountFetchAggsAction)
    private async fetchApplicationAggregationsAction(action: ApplicationAimStatusCountFetchAggsAction<AtsInterviewPortalExportHistoryFilter>) {
        const countResult = await this.network.atsApplicationCount(
            this.getWholeFilter(true, APPLICATION_AIM_STATUS_COUNT_FILTER_FIELDS_MAPPING),
            APPLICATION_AIM_STATUS_AGGREGATIONS,
            this.getOrgFilter()
        );
        const aggregations = countResult.Aggregations[APPLICATION_AIM_STATUS_AGGREGATIONS_RESULT_KEY] as { Items: StatusAggregationItem[] };
        action.aimStatusAggregation = aggregations?.Items ?? [];
    }

    get cacheKey(): string {
        return `manager-user:${this.state.currentUserId}:${this.keyPostfix}`;
    }

    private async loadPageList() {
        await this.loadPageData(this.state?.currentPreviousPageToken, this.state?.currentNextPageToken);
    }

    private async loadPageData(previousPageToken: string, nextPageToken: string) {
        if (this.listLoading) {
            return;
        }

        this.listLoading = true;
        const { pageSize, sort, search, filter, statusCounts, filterCounter } = this.state;
        const filterLength = filter ? Object.keys(filter).length : 0;
        let newStatusCounts = [];

        try {
            const historyResults = await this.network.atsInterviewPortalExportHistoryList(
                this.getWholeFilter(true, FILTER_FIELDS_MAPPING),
                search,
                sort,
                previousPageToken,
                nextPageToken,
                REQUIRED_GRID_FIELDS,
                AGGREGATIONS,
                false,
                this.getOrgFilter(),
                pageSize
            );
            if (historyResults.Aggregations) {
                newStatusCounts = historyResults?.Aggregations["terms_status"]?.Items;
            }
            if (typeof filter.status === "number" && filterLength === 1) {
                newStatusCounts = statusCounts;
            }

            if (typeof filter.status === "number" && filterLength > 1) {
                const historyResultsExceptStatus = await this.network.atsInterviewPortalExportHistoryList(
                    this.getWholeFilter(false, FILTER_FIELDS_MAPPING),
                    search,
                    sort,
                    previousPageToken,
                    nextPageToken,
                    REQUIRED_GRID_FIELDS,
                    AGGREGATIONS,
                    true,
                    this.getOrgFilter(),
                    pageSize
                );
                newStatusCounts = historyResultsExceptStatus.Aggregations["terms_status"]?.Items;
            }

            this.setState((state) => ({
                ...state,
                loaded: true,
                filterCounter: filterLength,
                statusCounts: newStatusCounts ?? [],
                previousPageToken: historyResults.PreviousPageToken,
                nextPageToken: historyResults.NextPageToken,
                currentPreviousPageToken: previousPageToken,
                currentNextPageToken: nextPageToken,
                list: historyResults.Results,
                total: historyResults.Total,
                updatedDate: new Date()
            }));

            if (filter.organization === undefined && !this.state.search) {
                this.setState((state) => ({
                    ...state,
                    organizationPageNumber: DEFAULT_PAGE_NUMBER,
                    organizationPageData: state.organizationList.slice(0, DEFAULT_PAGE_SIZE)
                }));
            }
        } catch (err) {
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true }));
        } finally {
            this.listLoading = false;
        }
    }

    @handle(LoadEvaluationPhaseAction)
    public async handleLoadEvaluationPhaseAction(action: LoadEvaluationPhaseAction) {
        action.season = await this.network.getSeasonById(action.id);
    }

    public getWholeFilter(withStatus = true, mapping: Map<string, string> = new Map()): string {
        const { filter } = this.state;

        if (!filter || !Object.keys(filter).length) return null;

        const filterArgs = [];
        for (const [key, value] of Object.entries(filter)) {
            if (value === undefined || value === null) continue;

            if (!withStatus && key === "status") continue;

            if (Array.isArray(value) && value.length) {
                (value as Array<string>).forEach((element) => {
                    filterArgs.push(`${mapping.get(key)}:${element}`);
                });
            } else {
                filterArgs.push(`${mapping.get(key)}:${value}`);
            }
        }

        return filterArgs.join(" AND ");
    }
    private getOrgFilter() {
        const { tags, type } = this.state.orgFilter;

        if (type === null && !tags?.length) return null;

        const filterArgs = [];
        if (type !== null) filterArgs.push(`metaData.type:${type}`);
        if (tags?.length) filterArgs.push(...tags.map((tag) => `tags:("${tag}")`));

        return filterArgs.join(" AND ");
    }

    // Client side pagination
    @handle(OrganizationPreviousAction)
    private handlePreviousAction(action: OrganizationPreviousAction) {
        const { organizationPageNumber, pageSize } = this.state;
        const previousPageNumber = Math.max(organizationPageNumber - 1, 1); // Should not be less than 1
        const sliceStart = Math.max(previousPageNumber - 1, 0) * pageSize;
        const sliceEnd = Math.max(previousPageNumber, 0) * pageSize;

        this.setState((state) => ({
            ...state,
            organizationPageNumber: previousPageNumber,
            organizationPageData: state.organizationList.slice(sliceStart, sliceEnd)
        }));
    }

    @handle(OrganizationNextAction)
    private handleNextAction(action: OrganizationNextAction) {
        const nextPage = this.state.organizationPageNumber + 1;
        this.setState((state) => ({
            ...state,
            organizationPageNumber: nextPage,
            organizationPageData: state.organizationList.slice(state.organizationPageNumber * state.pageSize, nextPage * state.pageSize)
        }));
    }
}
